import { useEffect } from 'react';
import { CustomFieldSchema, FieldProps, FieldWrapper } from 'common/form';
import { Loading } from 'components/Loading';
import { ListTeam } from 'generated-graphql/types';
import { TeamFieldState } from 'modules/candidate-profile/forms/schema';
import { TeamPickerField } from 'modules/candidate-profile/forms/components/TeamPickerField';
import { useListTeams } from 'modules/candidate-profile/hooks/useListTeams';
import { getTeamOptions } from 'modules/candidate-profile/utils/getTeamOptions';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';

type TeamFieldProps = FieldProps<CustomFieldSchema> & {
  currentTeams: ListTeam[];
  teamState: TeamFieldState;
};

// Loads the user's teams inside the open modal. Required while loading, so a
// list can't be saved before the teams are known.
export const TeamField: React.FC<TeamFieldProps> = (props) => {
  const { field, value, onChange, currentTeams, teamState } = props;
  const { isEnabled, teams, isLoading, isError } = useListTeams();
  const isSingleTeam = Boolean(useFlag(Flags.TALENT_LIST_SINGLE_TEAM));
  const options = getTeamOptions(teams, currentTeams);
  // A list given several teams through the API keeps them in single-team mode.
  const isReadOnly = isSingleTeam && currentTeams.length > 1;
  const isShown = isEnabled && (isLoading || isError || options.length > 0);

  teamState.isRequired = isShown && !isReadOnly;
  teamState.sendsTeams = isShown && !isReadOnly && !isLoading && !isError;

  // A new list starts in the user's team when they can pick exactly one.
  useEffect(() => {
    if (currentTeams.length === 0 && teams.length === 1 && !value?.length) {
      onChange([teams[0].id]);
    }
  }, [teams]);

  if (!isShown) {
    return null;
  }
  if (isLoading || isError) {
    return (
      <FieldWrapper
        label={field.label}
        errorMessage={isError ? 'Failed to load teams' : undefined}
      >
        {isLoading && <Loading size="24px" />}
      </FieldWrapper>
    );
  }

  return (
    <TeamPickerField
      {...props}
      options={options}
      isSingleTeam={isSingleTeam}
      isReadOnly={isReadOnly}
    />
  );
};
